/**
 * Launcher V2 A2 U6 — pure review-step logic: the two-bucket classification
 * and the approve-all Send/Save gate.
 *
 * Extracted from `ReviewStep.tsx` for the same reason `play-flow-coverage.ts`
 * and `config-page-logic.ts` were: `bun:test` has no DOM under `mini-apps/`,
 * so anything importing Glaze components can't be unit-tested there. This
 * module is DOM-free and is where U6's testable behavior lives.
 *
 * Everything here is PRESENTATION logic — which control is enabled, which
 * bucket label a row shows. Nothing here writes: the Send/Save mutations are
 * `mutations.ts` stubs (PR C / PR D).
 */

import type { PlaybooksMatchedMembersLadderResponse, SequenceMapping } from './schemas';

/**
 * The minimum a rung has to expose for review purposes — `PlaybookRung`
 * satisfies it structurally, and so does a `playbook-draft.ts#DraftRung` once
 * U3C's `ReviewStep` gives it a stand-in `id` (a persisted rung's real `id`,
 * or an unsaved one's `draftKey` — see that call site).
 *
 * Introduced (PR C, U3C) so `ReviewStep` can review the STAGED draft ladder —
 * including rungs added/removed/edited on Qualify but not yet saved — rather
 * than only the last-persisted `PlaybookRung[]`; without this, approving and
 * saving a freshly-added segmentation would be impossible (Review would show
 * zero rows for it until AFTER the save it exists to gate).
 */
export interface ReviewableRung {
  id: string;
  segmentationId: string;
  mapping: SequenceMapping | null;
  isCatchAll: boolean;
}

/**
 * EXACTLY two buckets (plan Key Decision 12). The prototype's third,
 * "Suppressed", is gone: a segmentation with no mapped sequence is simply
 * "No sequence" (Decision 13 — there is no suppression primitive, no
 * suppression badge, and no suppression vocabulary anywhere in V2).
 */
export type ReviewBucket = 'Routed' | 'No sequence';

/** Approval state, keyed by the rung's stable `id`. */
export type ReviewApprovals = Record<string, boolean>;

/**
 * The approvals map's key: a rung's stable `id` and nothing else.
 *
 * It exists as a named function rather than inline `rung.id` because the
 * approvals map is a CONTRACT between two places — the gate (`reviewGate`)
 * and the row that toggles a checkbox (`ReviewStep`) — and both sides have to
 * agree on the key. Everything else that merely needs a React `key` or a
 * selection key uses `rung.id` directly.
 */
export function rungKey(rung: ReviewableRung): string {
  return rung.id;
}

/**
 * A rung's bucket. `mapping === null` is the whole test — an unmapped
 * segmentation lands in "No sequence" with zero suppression vocabulary, and a
 * catch-all is classified by its mapping like any other rung.
 */
export function reviewBucket(rung: ReviewableRung): ReviewBucket {
  return rung.mapping === null ? 'No sequence' : 'Routed';
}

/** True when every listed key is explicitly approved (missing === unapproved). */
export function approvalsAllTrue(keys: readonly string[], approvals: ReviewApprovals): boolean {
  return keys.every((key) => approvals[key] === true);
}

export interface ReviewGate {
  /** Rungs approved, out of `totalCount`. */
  approvedCount: number;
  totalCount: number;
  allApproved: boolean;
  /**
   * Bucket tallies over the whole ladder, counting SEGMENTATIONS.
   *
   * These stay rung counts on purpose: `reviewGate` is the approve-all gate and
   * `anyRouted`/`isSendEnabled` are "is anything at all mapped?" questions, which
   * are about rungs, not members. The Review step's *displayed* Routed /
   * No-sequence numbers are MEMBER counts as of PR B U3 — see
   * {@link reviewMemberTally}, which is a separate function precisely so this
   * gate keeps working (unchanged, synchronously) while the ladder query is
   * still in flight.
   */
  routedCount: number;
  noSequenceCount: number;
  /** Is anything at all configured to send? Kept distinct from the approval count, as in the prototype. */
  anyRouted: boolean;
  isSendEnabled: boolean;
  isSaveEnabled: boolean;
  /** Plain-English "N groups still need approving above." — null once everything is approved. */
  unapprovedNote: string | null;
}

/**
 * The Send/Save gate.
 *
 * - **Send** needs every row approved AND something actually mapped — the
 *   prototype's `!anyRouted || approved < approvableRungs.length` guard, with
 *   "approvable" now meaning every rung (round 8B removed the suppression
 *   exclusion, and Decision 13 removed suppression itself).
 * - **Save** needs every row approved too. This is a deliberate departure from
 *   the prototype, where the plan-mode Save buttons were never gated: A2's
 *   locked behavior is that both Send and Save sit behind the approve-all
 *   gate. An empty ladder leaves Save disabled — there is nothing to approve
 *   and nothing to save.
 */
export function reviewGate(rungs: readonly ReviewableRung[], approvals: ReviewApprovals): ReviewGate {
  const keys = rungs.map(rungKey);
  const totalCount = keys.length;
  const approvedCount = keys.filter((key) => approvals[key] === true).length;
  const allApproved = approvalsAllTrue(keys, approvals);
  const routedCount = rungs.filter((rung) => reviewBucket(rung) === 'Routed').length;
  const noSequenceCount = totalCount - routedCount;
  const anyRouted = routedCount > 0;
  const outstanding = totalCount - approvedCount;

  return {
    approvedCount,
    totalCount,
    allApproved,
    routedCount,
    noSequenceCount,
    anyRouted,
    isSendEnabled: allApproved && anyRouted,
    isSaveEnabled: allApproved && totalCount > 0,
    unapprovedNote: allApproved
      ? null
      : `${outstanding} group${outstanding === 1 ? '' : 's'} still ${outstanding === 1 ? 'needs' : 'need'} approving above.`,
  };
}

/**
 * True when `fresh` — a playbook head freshly refetched during Apply's 409
 * retry (`playbook-draft-store.ts#persistLadderOnce`) — still matches what
 * `approved` was computed from, rung for rung: same id set, same
 * `segmentationId`, same `mapping`, same `isCatchAll`.
 *
 * Bug 2 fix (Codex adversarial review, 2026-09-02): `ReviewStep`'s "Apply to
 * campaigns" shares `persistLadder`'s single-flight-per-playbook queue and
 * 409-retry-once mechanism via an IDENTITY op — convenient, but it means a
 * 409's automatic retry re-applies that identity op against whatever the
 * refetched head now holds, with no re-check that the head is still the same
 * ladder the rep actually reviewed and approved. If another writer changed
 * the ladder (added/changed rungs) between the rep's review and their Apply
 * click, an unconditional retry would silently apply that NEW, never-reviewed
 * ladder to the campaign. This comparison is what `ReviewStep` gates the
 * retry on: a mismatch means "re-review before applying," not a silent apply.
 *
 * Mapping is compared via `JSON.stringify` — `SequenceMapping` is a plain,
 * JSON-safe object with no methods/dates/etc., so structural equality this
 * way is exact and doesn't need a deep-equal dependency for one comparison.
 */
export function ladderMatchesReviewedRungs(
  approved: readonly ReviewableRung[],
  fresh: readonly { id: string; segmentationId: string; mapping: SequenceMapping | null; isCatchAll: boolean }[],
): boolean {
  if (approved.length !== fresh.length) return false;
  const freshById = new Map(fresh.map((rung) => [rung.id, rung]));
  return approved.every((rung) => {
    const match = freshById.get(rung.id);
    return (
      match !== undefined &&
      match.segmentationId === rung.segmentationId &&
      match.isCatchAll === rung.isCatchAll &&
      JSON.stringify(match.mapping) === JSON.stringify(rung.mapping)
    );
  });
}

// PR B U3 (R5) — real member tallies
//
// A SIBLING pure function rather than extra parameters on `reviewGate`: the
// gate is synchronous presentation logic over data the Review step already has
// (rungs + approvals), while the member tally depends on an async ladder query
// that can still be loading, can fail, and can describe a DIFFERENT playbook
// version than the one currently rendered. Folding a "maybe absent, maybe
// stale" input into the gate would have made every existing gate caller carry
// a nullable it doesn't need.

export interface ReviewMemberTally {
  /**
   * Members claimed by a rung that HAS a sequence mapped — the real
   * "gets a send" population, first-match-wins.
   */
  routedCount: number;
  /**
   * Members claimed by a rung with `mapping === null`. They matched real
   * conditions; they just get no send (Decision 13 — an unmapped rung is a real
   * member-count exclusion, not just copy).
   */
  noSequenceCount: number;
  /**
   * Members no rung claimed at all — only possible when the ladder has no
   * catch-all. Broken out so `routedCount + noSequenceCount` not equalling the
   * roster size is explainable rather than mysterious.
   */
  unmatchedCount: number;
  /** Every member on the campaign, per the ladder — matched or not. */
  totalMembers: number;
  /**
   * The routed members themselves, ladder order. This is the set a send would
   * actually enroll; `ReviewStep` hands it to `sendEnrollments` instead of the
   * whole roster (the A2 comment saying it couldn't narrow this is now false).
   */
  routedMemberIds: string[];
}

/**
 * The Review step's Routed / No-sequence numbers as MEMBER counts.
 *
 * Buckets come from {@link reviewBucket} — the same, unchanged per-rung
 * classification the review rows render — joined to U2's ladder response by
 * the rung's stable `id`. The ladder's own `isMapped` is deliberately NOT used
 * as the classifier: one classification rule, one place.
 *
 * Returns `null` — meaning "no honest number yet", render a dash — when the
 * ladder is absent (loading/errored), describes a DIFFERENT playbook version
 * than the one rendered, or describes a ladder whose rung ids don't match the
 * rendered ones. Silently treating a missing rung as zero would under-report a
 * real send.
 *
 * Two independent staleness guards, both still needed now that the join is
 * id-keyed:
 *
 *  - `expectedPlaybookVersionId` checks the ladder's own `playbookVersionId`
 *    against the playbook version actually rendered — the two queries' real
 *    point of agreement. A reorder leaves the rung-id SET identical (ids never
 *    change; only `position` does), so an id-set comparison alone cannot tell
 *    a pre-reorder ladder from a post-reorder one.
 *  - each resolved ladder entry's `segmentationId` is cross-checked against
 *    the rendered rung's own, which catches a rung whose segmentation pointer
 *    was REPOINTED by an edit between the two reads even if a caller ever
 *    failed to thread the version id through.
 */
export function reviewMemberTally(
  rungs: readonly ReviewableRung[],
  ladder: PlaybooksMatchedMembersLadderResponse | undefined,
  expectedPlaybookVersionId: string | null,
): ReviewMemberTally | null {
  if (!ladder) return null;
  if (expectedPlaybookVersionId !== null && ladder.playbookVersionId !== expectedPlaybookVersionId) {
    return null;
  }

  const byRungId = new Map(ladder.rungs.map((entry) => [entry.rungId, entry]));
  if (byRungId.size !== ladder.rungs.length) return null;

  let routedCount = 0;
  let noSequenceCount = 0;
  const routedMemberIds: string[] = [];

  for (const rung of rungs) {
    const entry = byRungId.get(rungKey(rung));
    if (!entry) return null;
    // A rendered rung whose ladder entry names a DIFFERENT segmentation than
    // the one it actually points at means the two queries disagree about the
    // playbook (an edit repointed the rung between the reads), even if
    // `expectedPlaybookVersionId` somehow still matched.
    if (entry.segmentationId !== rung.segmentationId) return null;
    byRungId.delete(rungKey(rung));
    if (reviewBucket(rung) === 'Routed') {
      routedCount += entry.totalMatched;
      routedMemberIds.push(...entry.matchedMemberIds);
    } else {
      noSequenceCount += entry.totalMatched;
    }
  }

  // Anything left over is a rung the ladder knows about and the rendered
  // playbook doesn't — same drift, same answer.
  if (byRungId.size > 0) return null;

  return {
    routedCount,
    noSequenceCount,
    unmatchedCount: ladder.excludedMemberIds.length,
    totalMembers: ladder.totalMembers,
    routedMemberIds,
  };
}
